/** Pure geometry for the dependency-free line chart. */

export interface Pt {
  t: number
  v: number
}

export interface ChartGeometry {
  /** SVG path `d` of the line; empty when there is nothing to draw. */
  line: string
  /** Closed path under the line, for the faint fill. */
  area: string
  min: number
  max: number
  /** The y ticks as value + vertical position. */
  yTicks: { value: number; y: number }[]
  /** The x ticks as time + horizontal position. */
  xTicks: { t: number; x: number }[]
  /** The last point's position, for the end marker. */
  last: { x: number; y: number } | null
}

/** A "nice" upper bound so the top tick is a round number (1, 2, 5 x 10^n). */
export function niceMax(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1
  const exponent = Math.floor(Math.log10(max))
  const base = 10 ** exponent
  const fraction = max / base
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10
  return nice * base
}

export function layout(
  points: readonly Pt[],
  width: number,
  height: number,
  pad: { left: number; right: number; top: number; bottom: number },
  from: number,
  to: number,
): ChartGeometry {
  const innerW = Math.max(1, width - pad.left - pad.right)
  const innerH = Math.max(1, height - pad.top - pad.bottom)
  const peak = points.reduce((m, p) => (p.v > m ? p.v : m), 0)
  const max = niceMax(peak)
  const span = Math.max(1, to - from)
  const x = (t: number) => pad.left + ((t - from) / span) * innerW
  const y = (v: number) => pad.top + innerH - (v / max) * innerH

  const sorted = [...points].sort((a, b) => a.t - b.t)
  const coords = sorted.map((p) => [x(p.t), y(p.v)] as const)
  const line = coords.map(([cx, cy], i) => `${i === 0 ? 'M' : 'L'}${cx.toFixed(1)},${cy.toFixed(1)}`).join(' ')
  const first = coords[0]
  const lastCoord = coords[coords.length - 1]
  const baseline = pad.top + innerH
  const area =
    first === undefined || lastCoord === undefined
      ? ''
      : `${line} L${lastCoord[0].toFixed(1)},${baseline} L${first[0].toFixed(1)},${baseline} Z`

  const yTicks = [0, 0.5, 1].map((f) => ({ value: max * f, y: y(max * f) }))
  const xTicks = [0, 0.5, 1].map((f) => {
    const t = from + span * f
    return { t, x: x(t) }
  })

  return {
    line,
    area,
    min: 0,
    max,
    yTicks,
    xTicks,
    last: lastCoord === undefined ? null : { x: lastCoord[0], y: lastCoord[1] },
  }
}

/** Compact number for axis labels: 1200 -> "1.2k". */
export function compact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `${trim(value / 1_000_000_000)}G`
  if (abs >= 1_000_000) return `${trim(value / 1_000_000)}M`
  if (abs >= 1_000) return `${trim(value / 1_000)}k`
  return trim(value)
}

function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '')
}
